/* Art House header disclosures. Cart state remains owned by cart-drawer.js. */
(() => {
  'use strict';

  if (window.AlawooderHeaderNavigation) {
    window.AlawooderHeaderNavigation.init(document);
    return;
  }

  const instances = new Map();
  const menuSelector = 'details[data-ah-menu]';
  const focusableSelector = 'a[href],button,input,select,textarea,summary,[tabindex]';

  function isVisible(element) {
    return element instanceof HTMLElement && !element.closest('[hidden],[inert],[aria-hidden="true"]') &&
      element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
  }

  function focusableWithin(element) {
    return [...element.querySelectorAll(focusableSelector)].filter(candidate =>
      isVisible(candidate) && candidate.tabIndex >= 0 && !candidate.matches(':disabled'));
  }

  function modalIsOpen() {
    return Boolean(document.querySelector('dialog[open]'));
  }

  function rememberStyle(element, property) {
    return { element, property, value: element.style.getPropertyValue(property), priority: element.style.getPropertyPriority(property) };
  }

  function restoreStyle(saved) {
    if (saved.value) saved.element.style.setProperty(saved.property, saved.value, saved.priority);
    else saved.element.style.removeProperty(saved.property);
  }

  function mount(header) {
    if (instances.has(header)) return;
    const menus = [...header.querySelectorAll(menuSelector)].filter(menu => !menu.parentElement.closest(menuSelector));
    if (!menus.length) return;

    const mobileMenu = menus.find(menu => menu.matches('.ah-mobile-navigation'));
    const searchMenu = menus.find(menu => menu.matches('.ah-search-navigation'));
    const shopMenu = menus.find(menu => menu.matches('.ah-shop-navigation'));
    const compactMedia = matchMedia('(max-width: 1024px)');
    const hoverMedia = matchMedia('(hover: hover) and (pointer: fine)');
    const listeners = [];
    const inertSnapshots = new Map();
    const initialHeight = rememberStyle(document.documentElement, '--ah-header-height');
    let lockedStyles = null;
    let lockedScroll = null;
    let hadOpenClass = false;
    let heightFrame = 0;
    let focusFrame = 0;
    let hoverTimer;
    let linkCloseTimer;
    let shopOpenedByHover = false;
    let keyboardInteraction = false;
    let mobilePrepared = false;
    let destroyed = false;

    function listen(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      listeners.push(() => target.removeEventListener(type, handler, options));
    }

    function listenMedia(media, handler) {
      if (media.addEventListener) listen(media, 'change', handler);
      else {
        media.addListener(handler);
        listeners.push(() => media.removeListener(handler));
      }
    }

    function summaryOf(menu) {
      return menu?.querySelector('summary');
    }

    function panelOf(menu) {
      return menu?.querySelector('[data-ah-panel]');
    }

    function updateExpanded(menu) {
      const summary = summaryOf(menu);
      if (summary) {
        summary.setAttribute('aria-expanded', String(menu.open));
        if (menu === mobileMenu) summary.setAttribute('aria-label', menu.open ? (summary.dataset.ahLabelClose || 'Close navigation') : (summary.dataset.ahLabelOpen || 'Open navigation'));
      }
    }

    function writeHeight() {
      heightFrame = 0;
      if (destroyed || !header.isConnected) return;
      document.documentElement.style.setProperty('--ah-header-height', `${Math.max(0, Math.ceil((header.closest('.shopify-section-group-header-group') || header).getBoundingClientRect().bottom))}px`);
    }

    function scheduleHeight() {
      if (!heightFrame) heightFrame = requestAnimationFrame(writeHeight);
    }

    function lockPage() {
      if (lockedStyles) return;
      const html = document.documentElement;
      const body = document.body;
      lockedScroll = { left: window.scrollX, top: window.scrollY, hash: location.hash };
      const scrollbar = Math.max(0, window.innerWidth - html.clientWidth);
      lockedStyles = [rememberStyle(html, 'overflow'), rememberStyle(body, 'overflow')];
      if (scrollbar > 0) {
        lockedStyles.push(rememberStyle(body, 'padding-right'));
        body.style.paddingRight = `${(parseFloat(getComputedStyle(body).paddingRight) || 0) + scrollbar}px`;
      }
      hadOpenClass = html.classList.contains('ah-mobile-menu-open');
      html.classList.add('ah-mobile-menu-open');
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      document.querySelectorAll('main, .ah-footer').forEach(region => {
        if (header.contains(region) || region.closest('dialog')) return;
        inertSnapshots.set(region, { existed: region.hasAttribute('inert'), value: region.getAttribute('inert') });
        region.setAttribute('inert', '');
      });
      scheduleHeight();
    }

    function unlockPage() {
      if (!lockedStyles) return;
      lockedStyles.forEach(restoreStyle);
      lockedStyles = null;
      if (!hadOpenClass) document.documentElement.classList.remove('ah-mobile-menu-open');
      inertSnapshots.forEach((snapshot, region) => {
        if (snapshot.existed) region.setAttribute('inert', snapshot.value ?? '');
        else region.removeAttribute('inert');
      });
      inertSnapshots.clear();
      if (lockedScroll && lockedScroll.hash === location.hash) {
        window.scrollTo({ left: lockedScroll.left, top: lockedScroll.top, behavior: 'instant' });
      }
      lockedScroll = null;
      scheduleHeight();
    }

    function syncLock() {
      if (mobileMenu?.open && compactMedia.matches && !modalIsOpen()) lockPage();
      else unlockPage();
    }

    function cancelHover() {
      clearTimeout(hoverTimer);
      hoverTimer = undefined;
    }

    function cancelFocus() {
      if (focusFrame) cancelAnimationFrame(focusFrame);
      focusFrame = 0;
    }

    function cancelLinkClose() {
      clearTimeout(linkCloseTimer);
      linkCloseTimer = undefined;
    }

    function restoreMenuFocus(menu) {
      if (modalIsOpen()) return;
      const summary = summaryOf(menu);
      if (isVisible(summary)) summary.focus({ preventScroll: true });
    }

    function closeMenu(menu) {
      if (!menu) return;
      menu.open = false;
      if (menu === mobileMenu) mobilePrepared = false;
      if (menu === shopMenu) shopOpenedByHover = false;
      updateExpanded(menu);
    }

    function closeAll(options = {}) {
      cancelHover();
      cancelFocus();
      cancelLinkClose();
      const current = options.menu || menus.find(menu => menu.open && menu.contains(document.activeElement)) || menus.find(menu => menu.open);
      menus.forEach(closeMenu);
      syncLock();
      if (options.restoreFocus && current) restoreMenuFocus(current);
    }

    function focusSearch() {
      cancelFocus();
      focusFrame = requestAnimationFrame(() => {
        focusFrame = 0;
        if (destroyed || !searchMenu?.open || modalIsOpen()) return;
        const input = searchMenu.querySelector('input[type="search"], input[name="q"]');
        if (isVisible(input) && !input.disabled) input.focus({ preventScroll: true });
      });
    }

    function focusMobilePanel() {
      cancelFocus();
      focusFrame = requestAnimationFrame(() => {
        focusFrame = 0;
        if (destroyed || !mobileMenu?.open || modalIsOpen()) return;
        const panel = panelOf(mobileMenu);
        const target = panel && focusableWithin(panel)[0] || summaryOf(mobileMenu);
        if (isVisible(target)) target.focus({ preventScroll: true });
      });
    }

    function prepareMobileMenu() {
      if (!mobileMenu || mobilePrepared) return;
      mobilePrepared = true;
      const shop = mobileMenu.querySelector('details.ah-mobile-shop[data-ah-mobile-section]');
      if (shop) {
        shop.open = true;
        updateExpanded(shop);
      }
      const scrollArea = mobileMenu.querySelector('.ah-mobile-menu-scroll') || panelOf(mobileMenu);
      if (scrollArea) scrollArea.scrollTop = 0;
    }

    function openMenu(menu, options = {}) {
      if (!menu || destroyed || modalIsOpen()) return;
      if (menu === mobileMenu && !compactMedia.matches || menu === shopMenu && compactMedia.matches) return;
      cancelHover();
      cancelFocus();
      menus.forEach(other => { if (other !== menu) closeMenu(other); });
      if (menu === shopMenu) shopOpenedByHover = Boolean(options.hover);
      menu.open = true;
      updateExpanded(menu);
      if (menu === mobileMenu) prepareMobileMenu();
      syncLock();
      if (menu === searchMenu) focusSearch();
      else if (menu === mobileMenu && options.focus !== false) focusMobilePanel();
    }

    function onClick(event) {
      if (!(event.target instanceof Element)) return;
      const target = event.target;
      if (header.contains(target)) {
        if (target.closest('.ah-bag,[data-aw-cart-open]')) {
          // Capture runs before the existing cart click listener, with no preventDefault.
          closeAll();
          return;
        }
        const dismissal = target.closest('[data-ah-close],[data-ah-menu-backdrop]');
        if (dismissal) {
          event.preventDefault();
          closeAll({ restoreFocus: true });
          return;
        }
        const summary = target.closest('summary');
        const menu = summary?.parentElement;
        if (summary && menus.includes(menu)) {
          event.preventDefault();
          // A pointer click confirms a hover-opened menu; keyboard activation
          // (detail=0) keeps normal disclosure toggling.
          if (menu === shopMenu && menu.open && shopOpenedByHover && event.detail > 0) {
            shopOpenedByHover = false;
            cancelHover();
            return;
          }
          if (menu.open) closeAll({ restoreFocus: true, menu });
          else openMenu(menu);
          return;
        }
        const link = target.closest('a[href]');
        if (link) {
          // Cross-page links remain completely native: the destination document
          // closes the menu. Only in-page/home anchors need an explicit dismissal.
          cancelHover();
          cancelLinkClose();
          let localAnchor = false;
          try {
            const destination = new URL(link.href, location.href);
            const home = new URL(window.Alawooder?.routes?.root || '/', location.origin);
            localAnchor = Boolean(destination.hash && destination.origin === location.origin &&
              (destination.pathname === location.pathname || destination.pathname === home.pathname));
          } catch (error) { return; }
          if (!localAnchor) return;
          linkCloseTimer = setTimeout(() => {
            linkCloseTimer = undefined;
            if (!destroyed) closeAll();
          }, 150);
        }
      } else if (menus.some(menu => menu.open)) closeAll();
    }

    function onToggle(event) {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement)) return;
      if (details.matches('[data-ah-mobile-section]')) {
        updateExpanded(details);
        return;
      }
      if (!menus.includes(details)) return;
      if (details === mobileMenu && !details.open) mobilePrepared = false;
      updateExpanded(details);
      if (details.open) {
        if (modalIsOpen() || details === mobileMenu && !compactMedia.matches || details === shopMenu && compactMedia.matches) closeMenu(details);
        else {
          menus.forEach(other => { if (other !== details) closeMenu(other); });
          if (details === mobileMenu) prepareMobileMenu();
          if (details === searchMenu && !details.contains(document.activeElement)) focusSearch();
          if (details === mobileMenu && !header.contains(document.activeElement)) focusMobilePanel();
        }
      }
      syncLock();
    }

    function onKeydown(event) {
      keyboardInteraction = true;
      if (destroyed || modalIsOpen()) return;
      if (event.key === 'Escape' && menus.some(menu => menu.open)) {
        event.preventDefault();
        closeAll({ restoreFocus: true });
        return;
      }
      if (event.key !== 'Tab' || !mobileMenu?.open || !compactMedia.matches) return;
      const focusable = focusableWithin(header);
      if (!focusable.length) return;
      const active = document.activeElement;
      if (event.shiftKey && (active === focusable[0] || !focusable.includes(active))) {
        event.preventDefault();
        focusable[focusable.length - 1].focus({ preventScroll: true });
      } else if (!event.shiftKey && (active === focusable[focusable.length - 1] || !focusable.includes(active))) {
        event.preventDefault();
        focusable[0].focus({ preventScroll: true });
      }
    }

    function onFocusin(event) {
      if (modalIsOpen()) {
        closeAll();
        return;
      }
      if (keyboardInteraction && mobileMenu?.open && compactMedia.matches && !header.contains(event.target)) focusMobilePanel();
    }

    function onFocusout() {
      queueMicrotask(() => {
        if (destroyed) return;
        if (modalIsOpen()) { closeAll(); return; }
        // Safari pointer activation may blur a summary to body before the
        // anchor's click. Never hide its target or refocus during that sequence.
        if (!keyboardInteraction) return;
        if (!header.contains(document.activeElement)) {
          if (mobileMenu?.open && compactMedia.matches) focusMobilePanel();
          else closeAll();
        }
      });
    }

    function onBreakpoint() {
      const active = document.activeElement;
      const activeMenu = menus.find(menu => menu.contains(active));
      closeAll();
      if (header.contains(active) && !isVisible(active) && !modalIsOpen()) {
        const summary = summaryOf(activeMenu);
        const fallback = summaryOf(compactMedia.matches ? mobileMenu : shopMenu);
        const target = isVisible(summary) ? summary : isVisible(fallback) ? fallback : header.querySelector('.ah-logo');
        if (isVisible(target)) target.focus({ preventScroll: true });
      }
      scheduleHeight();
    }

    if (shopMenu) {
      listen(shopMenu, 'pointerenter', event => {
        if (!hoverMedia.matches || compactMedia.matches || event.pointerType !== 'mouse' || modalIsOpen()) return;
        cancelHover();
        if (searchMenu?.open || mobileMenu?.open) return;
        if (!shopMenu.open) openMenu(shopMenu, { focus: false, hover: true });
      });
      listen(shopMenu, 'pointerleave', event => {
        if (!hoverMedia.matches || compactMedia.matches || event.pointerType !== 'mouse') return;
        cancelHover();
        hoverTimer = setTimeout(() => {
          hoverTimer = undefined;
          if (destroyed || !shopMenu.open || keyboardInteraction && header.contains(document.activeElement)) return;
          closeMenu(shopMenu);
        }, 200);
      });
    }

    listen(document, 'click', onClick, true);
    listen(document, 'keydown', onKeydown, true);
    listen(document, 'focusin', onFocusin);
    listen(header, 'focusout', onFocusout);
    listen(header, 'toggle', onToggle, true);
    listen(header, 'pointerdown', () => { keyboardInteraction = false; });
    listen(document, 'aw:cart-updated', event => { if (event.detail?.source === 'product-form') closeAll(); });
    listen(document, 'aw:cart-error', event => { if (event.detail?.source === 'product-form') closeAll(); });
    listen(window, 'resize', scheduleHeight, { passive: true });
    listen(window, 'scroll', scheduleHeight, { passive: true });
    if (window.visualViewport) {
      listen(window.visualViewport, 'resize', scheduleHeight, { passive: true });
      listen(window.visualViewport, 'scroll', scheduleHeight, { passive: true });
    }
    listenMedia(compactMedia, onBreakpoint);
    listenMedia(hoverMedia, cancelHover);
    const resizeObserver = window.ResizeObserver ? new ResizeObserver(scheduleHeight) : null;
    resizeObserver?.observe(header);

    menus.forEach(updateExpanded);
    header.querySelectorAll('details[data-ah-mobile-section]').forEach(updateExpanded);
    syncLock();
    writeHeight();

    instances.set(header, {
      close: closeAll,
      destroy() {
        closeAll();
        destroyed = true;
        cancelHover();
        cancelFocus();
        if (heightFrame) cancelAnimationFrame(heightFrame);
        resizeObserver?.disconnect();
        listeners.forEach(remove => remove());
        restoreStyle(initialHeight);
        instances.delete(header);
      }
    });
  }

  function destroy(scope) {
    [...instances].forEach(([header, instance]) => {
      if (scope === header || scope.contains?.(header)) instance.destroy();
    });
  }

  function init(scope = document) {
    [...instances].forEach(([header, instance]) => { if (!header.isConnected) instance.destroy(); });
    if (scope.matches?.('[data-ah-header]')) mount(scope);
    scope.querySelectorAll?.('[data-ah-header]').forEach(mount);
  }

  window.AlawooderHeaderNavigation = { init, destroy, close: options => instances.forEach(instance => instance.close(options)) };
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
